import { StepKeysEnum, type ProductStepProps } from "../../steps/types";
import { WorkflowStep } from "./types";
import {
  AdjacentTeethStep as AdjacentTeethStepComponent,
  CrownTeethStep as CrownTeethStepComponent,
  ToothStep as ToothStepComponent,
  ArchStep as ArchStepComponent,
  MaterialCharacteristicsStep as MaterialStepComponent,
  InlayCoreCharacteristicsStep as InlayCoreStepComponent,
  ProductPreferencesStep as PreferencesStepComponent,
  TeethStep as TeethStepComponent,
  type TeethStepProps,
  WaxUpTypeStep as WaxUpTypeStepComponent,
  type ToothStepProps,
  ImplantConnectionStep as ImplantConnectionStepComponent,
  AbutmentStep as AbutmentStepComponent,
  ImplantRetentionStep as ImplantRetentionStepComponent,
  ImplantBridgeTeethStep as ImplantBridgeTeethStepComponent,
  InlayCoreCrownStep as InlayCoreCrownStepComponent,
  type MaterialCharacteristicsStepProps,
  type CrownTeethStepProps,
  SplintTypeStep as SplintTypeStepComponent,
  type ArchStepProps,
} from "../../steps";
import type { WorkflowProduct } from "./ProductWorkflowConfiguration";

export class AdjacentTeethStep extends WorkflowStep<
  typeof StepKeysEnum.adjacentTeeth
> {
  readonly key = StepKeysEnum.adjacentTeeth;
  readonly label = "Dents concernées";
  readonly Component = AdjacentTeethStepComponent;
}

export class ImplantBridgeTeethStep extends WorkflowStep<
  typeof StepKeysEnum.implantBridgeTeeth
> {
  readonly key = StepKeysEnum.implantBridgeTeeth;
  readonly label = "Dents concernées";
  readonly Component = ImplantBridgeTeethStepComponent;
}

export class CrownTeethStep extends WorkflowStep<
  typeof StepKeysEnum.crownTeeth
> {
  readonly key = StepKeysEnum.crownTeeth;
  readonly label = "Dents concernées";
  readonly props: Pick<CrownTeethStepProps, "type">;

  readonly Component = (stepProps: ProductStepProps) => (
    <CrownTeethStepComponent {...this.props} {...stepProps} />
  );

  constructor(props: Pick<CrownTeethStepProps, "type">) {
    super();
    this.props = props;
  }
}

export class ToothStep extends WorkflowStep<typeof StepKeysEnum.tooth> {
  readonly key = StepKeysEnum.tooth;
  readonly label = "Dent concernée";
  readonly props: Partial<ToothStepProps> = {};
  readonly Component = (stepProps: ProductStepProps) => (
    <ToothStepComponent {...this.props} {...stepProps} />
  );

  constructor(props?: Partial<ToothStepProps>) {
    super();
    this.props = { ...this.props, ...(props ?? {}) };
  }
}

export class ArchStep extends WorkflowStep<typeof StepKeysEnum.arch> {
  readonly key = StepKeysEnum.arch;
  readonly label = "Arcade concernée";
  readonly props: Partial<ArchStepProps> = {};

  readonly Component = (stepProps: ProductStepProps) => (
    <ArchStepComponent {...this.props} {...stepProps} />
  );

  constructor(props?: Partial<ArchStepProps>) {
    super();
    this.props = { ...this.props, ...(props ?? {}) };
  }
}

export class MaterialStep extends WorkflowStep<typeof StepKeysEnum.material> {
  readonly key = StepKeysEnum.material;
  readonly label = "Matériau & teinte";
  readonly Component = (stepProps: ProductStepProps) => (
    <MaterialStepComponent {...{ ...stepProps, ...this.props }} />
  );

  readonly props: Partial<MaterialCharacteristicsStepProps> = {};

  constructor(args?: {
    props?: Partial<MaterialCharacteristicsStepProps>;
    isSkipped?: (product: WorkflowProduct) => boolean;
  }) {
    super();
    this.props = { ...this.props, ...(args?.props ?? {}) };
    if (args?.isSkipped) {
      this.isSkipped = args.isSkipped;
    }
  }
}

export class InlayCoreStep extends WorkflowStep<typeof StepKeysEnum.inlayCore> {
  readonly key = StepKeysEnum.inlayCore;
  readonly label = "Caractéristiques de l'inlay-core";
  readonly Component = InlayCoreStepComponent;
}

export class PreferencesStep extends WorkflowStep<
  typeof StepKeysEnum.preferences
> {
  readonly key = StepKeysEnum.preferences;
  readonly label = "Consignes";
  readonly Component = PreferencesStepComponent;
  readonly optional = true;
}

export class TeethStep extends WorkflowStep<typeof StepKeysEnum.teeth> {
  readonly key = StepKeysEnum.teeth;
  readonly label = "Dents concernées";
  readonly props: Partial<TeethStepProps> = {};
  readonly Component = (stepProps: ProductStepProps) => (
    <TeethStepComponent {...this.props} {...stepProps} />
  );

  constructor(props?: Partial<TeethStepProps>) {
    super();
    this.props = { ...this.props, ...(props ?? {}) };
  }
}

export class WaxUpTypeStep extends WorkflowStep<typeof StepKeysEnum.waxUpType> {
  readonly key = StepKeysEnum.waxUpType;
  readonly label = "Type de wax-up";
  readonly Component = WaxUpTypeStepComponent;
}

export class ImplantConnectionStep extends WorkflowStep<
  typeof StepKeysEnum.implantConnection
> {
  readonly key = StepKeysEnum.implantConnection;
  readonly label = "Connectique implantaire";
  readonly Component = ImplantConnectionStepComponent;
}

export class AbutmentStep extends WorkflowStep<typeof StepKeysEnum.abutment> {
  readonly key = StepKeysEnum.abutment;
  readonly label = "Pilier & Analogue";
  readonly Component = AbutmentStepComponent;
}

export class ImplantRetentionStep extends WorkflowStep<
  typeof StepKeysEnum.implantRetention
> {
  readonly key = StepKeysEnum.implantRetention;
  readonly label = "Mode de rétention";
  readonly Component = ImplantRetentionStepComponent;
}

export class InlayCoreCrownStep extends WorkflowStep<
  typeof StepKeysEnum.inlayCoreCrown
> {
  readonly key = StepKeysEnum.inlayCoreCrown;
  readonly label = "Couronne";
  readonly Component = InlayCoreCrownStepComponent;
}

export class SplintTypeStep extends WorkflowStep<
  typeof StepKeysEnum.splintType
> {
  readonly key = StepKeysEnum.splintType;
  readonly label = "Type de gouttière";
  readonly Component = SplintTypeStepComponent;
}
